import { useEffect, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  CircleHelp,
  Database,
  Flame,
  Library,
  Mail,
  MessageSquare,
  MessageSquareText,
  Sparkles,
  ThumbsDown,
  ThumbsUp,
  TrendingUp,
  TriangleAlert,
  Trophy,
  Wand2,
} from 'lucide-react';
import { Card, IconTile, Spinner } from '@/components/ui';
import { Veckostaplar, type Veckopunkt } from '@/components/features/insikter/Veckostaplar';
import { StatCard } from '@/components/features/insikter/StatCard';
import { LedningsrapportFlik } from '@/components/features/insikter/LedningsrapportFlik';
import { useAdmin } from '@/hooks/useAdmin';
import { veckonummer } from '@/lib/veckor';
import type { DashboardStats } from '@/lib/types';
import { useRaknaUpp } from '@/hooks/useRaknaUpp';
import { supabase } from '@/lib/supabase';
import { formatDate, cn } from '@/lib/utils';

type InsightsTab = 'elvis' | 'elvira' | 'ledningsrapport';

/** ?flik=… väljer fliken – mejlet med ledningsrapporten länkar till ?flik=ledningsrapport. */
function tolkaFlik(v: string | null): InsightsTab {
  return v === 'elvira' || v === 'ledningsrapport' ? v : 'elvis';
}

type Stats = DashboardStats;

interface FaqRow {
  id: string;
  question_text: string;
  count: number;
  last_asked: string;
}

type UnansweredOutcome = 'partial' | 'unanswered' | 'error';

interface UnansweredRow {
  id: string;
  question_text: string;
  top_match_filename: string | null;
  top_match_similarity: number | null;
  created_at: string;
  notified: boolean;
  outcome: UnansweredOutcome;
  gaps_text: string | null;
  error_message: string | null;
}

interface SourceRow {
  filename: string;
  chunk_count: number;
}

interface MailStats {
  total_uses: number;
  uses_last_7d: number;
  feedback_up: number;
  feedback_down: number;
  answers_with_gaps: number;
}

/** insikter_veckoserier(): tolv veckor per serie, måndag svensk tid. */
interface Veckoserier {
  /** Måndagen (svensk tid) i veckan då ELvis-loggen började; null innan första frågan. */
  elvis_start_vecka: string | null;
  serier: { elvis: Veckopunkt[]; elvira: Veckopunkt[]; luckor_nya: Veckopunkt[] };
}

interface MailFeedbackRow {
  id: string;
  rating: 'up' | 'down';
  comment: string | null;
  created_at: string;
}

export function InsightsPage() {
  const [sokParametrar, setSokParametrar] = useSearchParams();
  const tab = tolkaFlik(sokParametrar.get('flik'));
  const setTab = (v: InsightsTab) =>
    setSokParametrar(
      (fore) => {
        const nya = new URLSearchParams(fore);
        if (v === 'elvis') nya.delete('flik');
        else nya.set('flik', v);
        return nya;
      },
      { replace: true },
    );
  const { isAdmin } = useAdmin();
  const [stats, setStats] = useState<Stats | null>(null);
  const [mailStats, setMailStats] = useState<MailStats | null>(null);
  const [topFaqs, setTopFaqs] = useState<FaqRow[]>([]);
  const [unanswered, setUnanswered] = useState<UnansweredRow[]>([]);
  const [topSources, setTopSources] = useState<SourceRow[]>([]);
  const [mailFeedback, setMailFeedback] = useState<MailFeedbackRow[]>([]);
  const [veckor, setVeckor] = useState<Veckoserier | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const since7d = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();

      const [
        statsRes,
        faqRes,
        unansweredRes,
        sourcesRes,
        mailTotal,
        mail7d,
        feedbackRes,
        mailFeedbackRes,
        mailGapsRes,
        veckorRes,
      ] = await Promise.all([
        supabase.rpc('dashboard_stats'),
        supabase
          .from('ai_questions')
          .select('id, question_text, count, last_asked')
          .order('count', { ascending: false })
          .limit(10),
        supabase
          .from('ai_unanswered')
          .select(
            'id, question_text, top_match_filename, top_match_similarity, created_at, notified, outcome, gaps_text, error_message',
          )
          .order('created_at', { ascending: false })
          .limit(50),
        supabase.rpc('list_kb_sources'),
        supabase.from('mail_assistant_logs').select('id', { count: 'exact', head: true }),
        supabase
          .from('mail_assistant_logs')
          .select('id', { count: 'exact', head: true })
          .gte('created_at', since7d),
        supabase.from('answer_feedback').select('rating').eq('feature', 'mail_assistant'),
        supabase
          .from('answer_feedback')
          .select('id, rating, comment, created_at')
          .eq('feature', 'mail_assistant')
          .order('created_at', { ascending: false })
          .limit(15),
        supabase
          .from('mail_assistant_logs')
          .select('id', { count: 'exact', head: true })
          .gt('gaps_count', 0),
        // Veckoserierna är en förstärkning: saknas funktionen eller fallerar den
        // visas korten som förut, utan staplar.
        supabase.rpc('insikter_veckoserier'),
      ]);

      if (statsRes.data) setStats(statsRes.data as Stats);
      if (faqRes.data) setTopFaqs(faqRes.data as FaqRow[]);
      if (unansweredRes.data) setUnanswered(unansweredRes.data as UnansweredRow[]);
      if (sourcesRes.data) {
        const sorted = (sourcesRes.data as SourceRow[])
          .slice()
          .sort((a, b) => b.chunk_count - a.chunk_count)
          .slice(0, 10);
        setTopSources(sorted);
      }
      if (mailFeedbackRes.data) setMailFeedback(mailFeedbackRes.data as MailFeedbackRow[]);
      if (veckorRes.data) setVeckor(veckorRes.data as Veckoserier);

      const feedback = (feedbackRes.data ?? []) as Array<{ rating: string }>;
      setMailStats({
        total_uses: mailTotal.count ?? 0,
        uses_last_7d: mail7d.count ?? 0,
        feedback_up: feedback.filter((f) => f.rating === 'up').length,
        feedback_down: feedback.filter((f) => f.rating === 'down').length,
        answers_with_gaps: mailGapsRes.count ?? 0,
      });

      setLoading(false);
    })();
  }, []);

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Spinner size={28} className="text-brand-500" />
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto p-5 lg:p-6">
      <div className="max-w-[1400px] mx-auto pb-10 space-y-4">
        {/* Sidans namn står i topbaren — här räcker en rad som säger vad man ser. */}
        <header className="flex items-center justify-between gap-4">
          <p className="text-[12.5px] text-ink-500">
            {tab === 'elvis'
              ? 'ELvis · chatt-AI:n, kunskapsbasen och obesvarade frågor'
              : tab === 'elvira'
                ? 'Elvira · mail-assistentens användning och feedback'
                : 'Ledningsrapport · månadens siffror, frysta när rapporten skapas'}
          </p>
          {/* Ledningsrapporten är fryst – "Realtid" vore fel där. */}
          {tab !== 'ledningsrapport' && (
            <div className="hidden sm:flex items-center gap-1.5 text-[11px] text-ink-500 shrink-0">
              <Sparkles size={12} strokeWidth={2} />
              <span>Realtid</span>
            </div>
          )}
        </header>

        <div className="flex max-w-full overflow-x-auto">
          <InsightsTabs value={tab} onChange={setTab} />
        </div>

        {tab === 'ledningsrapport' ? (
          <LedningsrapportFlik arAdmin={isAdmin} />
        ) : tab === 'elvis' ? (
          <ElvisInsights
            stats={stats}
            veckor={veckor}
            topFaqs={topFaqs}
            unanswered={unanswered}
            topSources={topSources}
          />
        ) : (
          <ElviraInsights mailStats={mailStats} mailFeedback={mailFeedback} veckor={veckor} />
        )}
      </div>
    </div>
  );
}

function InsightsTabs({
  value,
  onChange,
}: {
  value: InsightsTab;
  onChange: (v: InsightsTab) => void;
}) {
  const options: Array<{ value: InsightsTab; label: string; icon: ReactNode }> = [
    { value: 'elvis', label: 'ELvis (chatt)', icon: <Wand2 size={14} strokeWidth={2} /> },
    { value: 'elvira', label: 'Elvira (mail)', icon: <Mail size={14} strokeWidth={2} /> },
    { value: 'ledningsrapport', label: 'Ledningsrapport', icon: <RapportGlyf /> },
  ];
  return (
    <div className="inline-flex items-center gap-0.5 p-0.5 rounded-md bg-ink-100 border border-ink-200">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          aria-pressed={value === o.value}
          onClick={() => onChange(o.value)}
          className={cn(
            // Under sm faller ikonerna bort så att tre flikar ryms på 390 px.
            'inline-flex items-center gap-2 px-2.5 sm:px-3 py-1.5 rounded text-[12px] font-medium transition-colors whitespace-nowrap',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500',
            value === o.value
              ? 'bg-surface text-ink-900 border border-ink-200'
              // ink-600, inte ink-500: texten ligger på den tonade banan
              // (ink-100), inte mot vitt, och ink-500 gav där 4,36:1 — precis
              // under AA. Mönstret finns i flera lokala kopior utöver den delade
              // SegmentedControl (här, SidePanel, AIChat, MailAssistantPage);
              // alla är höjda, men de borde slås ihop någon gång.
              : 'text-ink-600 hover:text-ink-900',
          )}
        >
          <span className="hidden sm:inline-flex">{o.icon}</span>
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** Egen glyf, inte Lucide: ett blad med tre rader, samma 14 px och streck som flikens ikoner. */
function RapportGlyf() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden>
      <rect x="2.75" y="1.75" width="8.5" height="10.5" rx="1.25" />
      <path d="M5 5h4M5 7.25h4M5 9.5h2.25" strokeLinecap="round" />
    </svg>
  );
}

/* ============================== ELVIS ============================== */

function ElvisInsights({
  stats,
  veckor,
  topFaqs,
  unanswered,
  topSources,
}: {
  stats: Stats | null;
  veckor: Veckoserier | null;
  topFaqs: FaqRow[];
  unanswered: UnansweredRow[];
  topSources: SourceRow[];
}) {
  return (
    <div className="space-y-5 animate-fade-in">
      {/* HERO row */}
      <div className="grid grid-cols-12 gap-5">
        <div className="col-span-12 lg:col-span-7">
          <HeroCard
            label="Total användning"
            value={stats?.total_questions ?? 0}
            subValue={stats?.unique_questions ?? 0}
            subLabel="olika frågor (unika)"
            delta={stats?.questions_last_7d ?? 0}
            deltaLabel="aktiva senaste 7 dagarna"
            icon={<MessageSquareText size={14} strokeWidth={1.75} />}
            tone="brand"
            trend={elvisTrend(veckor)}
          />
        </div>
        <div className="col-span-12 lg:col-span-5">
          <HeroCard
            label="Kunskapsluckor"
            value={stats?.unanswered_total ?? 0}
            subValue={null}
            subLabel={stats?.unanswered_total === 0 ? 'Alla frågor besvarade' : 'obesvarade totalt'}
            delta={stats?.unanswered_last_7d ?? 0}
            deltaLabel="senaste 7 dagar"
            icon={<TriangleAlert size={14} strokeWidth={1.75} />}
            tone={(stats?.unanswered_total ?? 0) > 0 ? 'warning' : 'success'}
            compact
            trend={
              veckor ? <Veckostaplar serie={veckor.serier.luckor_nya} enhet="nya luckor" rubrik="nya per vecka" /> : undefined
            }
          />
        </div>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-12 gap-5">
        <div className="col-span-6 lg:col-span-4">
          <StatCard
            label="Källor i kunskapsbas"
            value={stats?.source_count ?? 0}
            sub={`${stats?.chunk_count.toLocaleString('sv-SE') ?? 0} chunks totalt`}
            icon={<Library size={14} strokeWidth={1.75} />}
            tone="neutral"
          />
        </div>
        <div className="col-span-6 lg:col-span-4">
          <StatCard
            label="Kretskampen-omgångar"
            value={stats?.quiz_scores_total ?? 0}
            sub="spelomgångar spelade"
            icon={<Trophy size={14} strokeWidth={1.75} />}
            tone="success"
          />
        </div>
        <div className="col-span-12 lg:col-span-4">
          <StatCard
            label="Senaste 7 dagar"
            value={stats?.questions_last_7d ?? 0}
            sub={`${stats?.unanswered_last_7d ?? 0} av dem obesvarade`}
            icon={<TrendingUp size={14} strokeWidth={1.75} />}
            tone="brand"
          />
        </div>
      </div>

      {/* BENTO row */}
      <div className="grid grid-cols-12 gap-5">
        <div className="col-span-12 lg:col-span-8">
          <Card variant="glass" className="p-6 h-full">
            <SectionHeader
              icon={<CircleHelp size={14} strokeWidth={1.75} />}
              tone="warning"
              title="Senaste obesvarade"
              subtitle="Frågor ELvis inte kunde svara fullt på — fyll luckan i Kunskapsbasen"
            />
            {unanswered.length === 0 ? (
              <EmptyState text="Inga obesvarade frågor än. ELvis klarar alla frågor med nuvarande kunskapsbas." />
            ) : (
              <ul className="space-y-2 max-h-[560px] overflow-y-auto pr-1 -mr-1">
                {unanswered.map((row) => (
                  <li
                    key={row.id}
                    className="p-3.5 rounded-xl bg-surface border border-ink-100 hover:border-amber-200 hover:bg-amber-50/40 transition-colors"
                  >
                    <div className="flex items-start gap-2">
                      <OutcomeBadge outcome={row.outcome} />
                      <p className="flex-1 text-[13.5px] font-semibold text-ink-900 leading-snug">
                        {row.question_text}
                      </p>
                    </div>
                    {row.outcome === 'partial' && row.gaps_text && (
                      <p className="mt-2 text-[12px] text-amber-900 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 leading-relaxed">
                        {row.gaps_text}
                      </p>
                    )}
                    {row.outcome === 'error' && row.error_message && (
                      <p className="mt-2 text-[12px] text-red-800 bg-red-50 border border-red-100 rounded-lg px-3 py-2 leading-relaxed">
                        {row.error_message}
                      </p>
                    )}
                    <div className="mt-2 flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider text-ink-500 flex-wrap">
                      <span>
                        {formatDate(row.created_at, {
                          day: 'numeric',
                          month: 'short',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </span>
                      {row.top_match_similarity != null && (
                        <>
                          <span className="opacity-40">·</span>
                          <span>närmast: {Math.round(row.top_match_similarity * 100)} %</span>
                        </>
                      )}
                      {row.top_match_filename && (
                        <>
                          <span className="opacity-40">·</span>
                          <span className="truncate max-w-[240px] normal-case tracking-normal text-ink-500 font-semibold">
                            {row.top_match_filename}
                          </span>
                        </>
                      )}
                      {row.notified && (
                        <>
                          <span className="opacity-40">·</span>
                          <span className="text-emerald-600 flex items-center gap-1">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                            mail skickat
                          </span>
                        </>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>

        <div className="col-span-12 lg:col-span-4">
          <Card variant="glass" className="p-6 h-full">
            <SectionHeader
              icon={<Flame size={14} strokeWidth={1.75} />}
              tone="warning"
              title="Populärast"
              subtitle="Mest ställda frågor"
            />
            {topFaqs.length === 0 ? (
              <EmptyState text="Inga frågor än — börja chatta." />
            ) : (
              <ul className="space-y-1.5">
                {topFaqs.map((faq, i) => (
                  <li
                    key={faq.id}
                    className="flex items-center gap-3 p-2.5 rounded-xl bg-surface border border-ink-100 hover:border-brand-400 hover:bg-brand-50/40 transition-colors"
                  >
                    <span
                      className={cn(
                        'w-7 h-7 rounded-lg inline-flex items-center justify-center font-display text-sm shrink-0 tabular-nums',
                        i === 0
                          ? 'bg-amber-50 text-amber-700 ring-1 ring-amber-200'
                          : i < 3
                            ? 'bg-ink-100 text-ink-700'
                            : 'bg-ink-50 text-ink-500',
                      )}
                    >
                      {i + 1}
                    </span>
                    <span className="flex-1 min-w-0 text-[12px] font-semibold text-ink-700 truncate">
                      {faq.question_text}
                    </span>
                    <span className="shrink-0 text-[10px] font-bold px-2 py-0.5 rounded-full bg-brand-50 text-brand-700 border border-brand-100 tabular-nums">
                      {faq.count}×
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>
      </div>

      {/* Sources */}
      <Card variant="glass" className="p-6">
        <SectionHeader
          icon={<Database size={14} strokeWidth={1.75} />}
          tone="neutral"
          title="Största källor"
          subtitle="Dokument med flest indexerade chunks — visar vilka som driver ELvis svar"
        />
        {topSources.length === 0 ? (
          <EmptyState text="Inga källor än." />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
            {topSources.map((src, i) => {
              const maxChunks = topSources[0]?.chunk_count || 1;
              const pct = (src.chunk_count / maxChunks) * 100;
              const isUrl =
                src.filename.includes('/') ||
                src.filename.includes('.se') ||
                src.filename.includes('.eu');
              const displayName = isUrl ? src.filename : src.filename.replace(/\.[^/.]+$/, '');
              return (
                <div key={src.filename}>
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className="text-[10px] font-semibold text-ink-500 tabular-nums w-6 shrink-0">
                      {i + 1}
                    </span>
                    <span
                      className="flex-1 min-w-0 text-[12.5px] font-semibold text-ink-700 truncate"
                      title={src.filename}
                    >
                      {displayName}
                    </span>
                    <span className="shrink-0 text-[11px] font-bold text-ink-800 tabular-nums">
                      {src.chunk_count}
                    </span>
                  </div>
                  <div className="h-1.5 bg-ink-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-brand-500 rounded-full transition-all duration-500 ease-out"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
}

function OutcomeBadge({ outcome }: { outcome: UnansweredOutcome }) {
  const map: Record<UnansweredOutcome, { label: string; cls: string }> = {
    unanswered: { label: 'Obesvarad', cls: 'bg-red-100 text-red-800 border-red-200' },
    partial: { label: 'Delvis', cls: 'bg-amber-100 text-amber-800 border-amber-200' },
    error: { label: 'Tekniskt fel', cls: 'bg-ink-200 text-ink-700 border-ink-300' },
  };
  const m = map[outcome] ?? map.unanswered;
  return (
    <span
      className={cn(
        'shrink-0 mt-0.5 inline-flex items-center px-2 py-0.5 rounded-md text-[9px] font-semibold uppercase tracking-wider border',
        m.cls,
      )}
    >
      {m.label}
    </span>
  );
}

/* ============================== ELVIRA ============================== */

function ElviraInsights({
  mailStats,
  mailFeedback,
  veckor,
}: {
  mailStats: MailStats | null;
  mailFeedback: MailFeedbackRow[];
  veckor: Veckoserier | null;
}) {
  return (
    <div className="space-y-5 animate-fade-in">
      {/* HERO + KPI */}
      <div className="grid grid-cols-12 gap-5">
        <div className="col-span-12 lg:col-span-7">
          <HeroCard
            label="Mailsvar genererade"
            value={mailStats?.total_uses ?? 0}
            subValue={null}
            subLabel="färdiga svarsförslag totalt"
            delta={mailStats?.uses_last_7d ?? 0}
            deltaLabel="senaste 7 dagarna"
            icon={<Mail size={14} strokeWidth={1.75} />}
            tone="brand"
            trend={veckor ? <Veckostaplar serie={veckor.serier.elvira} enhet="utkast" rubrik="utkast per vecka" /> : undefined}
          />
        </div>
        <div className="col-span-12 lg:col-span-5">
          <HeroCard
            label="Svar med kunskapsluckor"
            value={mailStats?.answers_with_gaps ?? 0}
            subValue={null}
            subLabel={
              (mailStats?.answers_with_gaps ?? 0) === 0
                ? 'Inga luckor flaggade'
                : 'svar som behövde manuell komplettering'
            }
            delta={0}
            deltaLabel="Elvira flaggade dessa innan sändning"
            icon={<TriangleAlert size={14} strokeWidth={1.75} />}
            tone={(mailStats?.answers_with_gaps ?? 0) > 0 ? 'warning' : 'success'}
            compact
            hideDelta
          />
        </div>
      </div>

      <div className="grid grid-cols-12 gap-5">
        <div className="col-span-6 lg:col-span-4">
          <StatCard
            label="Senaste 7 dagar"
            value={mailStats?.uses_last_7d ?? 0}
            sub="genererade svar"
            icon={<TrendingUp size={14} strokeWidth={1.75} />}
            tone="brand"
          />
        </div>
        <div className="col-span-6 lg:col-span-4">
          <StatCard
            label="Positiv feedback"
            value={mailStats?.feedback_up ?? 0}
            sub={feedbackRatioText(mailStats)}
            icon={<ThumbsUp size={14} strokeWidth={1.75} />}
            tone="success"
          />
        </div>
        <div className="col-span-12 lg:col-span-4">
          <StatCard
            label="Negativ feedback"
            value={mailStats?.feedback_down ?? 0}
            sub={
              mailStats && mailStats.feedback_down > 0
                ? 'Granska för kvalitetsförbättring'
                : 'Inga än'
            }
            icon={<ThumbsDown size={14} strokeWidth={1.75} />}
            tone={(mailStats?.feedback_down ?? 0) > 0 ? 'warning' : 'neutral'}
          />
        </div>
      </div>

      {/* Feedback feed */}
      <Card variant="glass" className="p-6">
        <SectionHeader
          icon={<MessageSquare size={14} strokeWidth={1.75} />}
          tone="brand"
          title="Senaste feedback på Elviras svar"
          subtitle="Positiva och negativa omdömen medarbetarna gett — kommentarerna visar vad som kan förbättras"
        />
        {mailFeedback.length === 0 ? (
          <EmptyState text="Ingen feedback på mailsvar än. Tummen upp/ner under varje svar hamnar här." />
        ) : (
          <ul className="space-y-2">
            {mailFeedback.map((row) => (
              <li
                key={row.id}
                className={cn(
                  'p-3.5 rounded-xl bg-surface border transition-colors',
                  row.rating === 'up'
                    ? 'border-emerald-100 hover:border-emerald-200'
                    : 'border-red-100 hover:border-red-200',
                )}
              >
                <div className="flex items-center gap-2.5">
                  <span
                    className={cn(
                      'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wider shrink-0',
                      row.rating === 'up'
                        ? 'bg-emerald-100 text-emerald-800'
                        : 'bg-red-100 text-red-800',
                    )}
                  >
                    {row.rating === 'up' ? (
                      <ThumbsUp size={11} strokeWidth={2.5} />
                    ) : (
                      <ThumbsDown size={11} strokeWidth={2.5} />
                    )}
                    {row.rating === 'up' ? 'Bra svar' : 'Dåligt svar'}
                  </span>
                  <span className="text-[10px] font-bold uppercase tracking-wider text-ink-500">
                    {formatDate(row.created_at, {
                      day: 'numeric',
                      month: 'short',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </span>
                </div>
                {row.comment && (
                  <p className="mt-2 text-[13px] text-ink-700 leading-relaxed">"{row.comment}"</p>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

/* ============================== SHARED ============================== */

function feedbackRatioText(stats: MailStats | null): string {
  if (!stats) return '—';
  const total = stats.feedback_up + stats.feedback_down;
  if (total === 0) return 'Ingen feedback än';
  const pct = Math.round((stats.feedback_up / total) * 100);
  return `${pct}% positiva av ${total} röster`;
}

/**
 * ELvis-frågor loggas per tillfälle först sedan händelseloggen kom på plats.
 * Innan den har en hel vecka bakom sig vore en kurva bara nollor följt av en
 * stapel – och nollorna skulle ljuga om att ingen frågade. Då visas i stället
 * från vilken vecka kurvan fylls på.
 */
function elvisTrend(veckor: Veckoserier | null): ReactNode {
  if (!veckor) return undefined;
  const start = veckor.elvis_start_vecka;
  if (!start) {
    return <TrendPlatshallare text="Veckokurvan fylls på från och med nu" />;
  }
  // Båda är måndagar i svensk tid (från samma RPC) – en ren veckojämförelse.
  const fran = veckor.serier.elvis.filter((p) => p.vecka >= start);
  if (fran.length < 4) {
    return <TrendPlatshallare text={`Veckokurvan fylls på sedan vecka ${veckonummer(start)}`} />;
  }
  return <Veckostaplar serie={fran} enhet="frågor" rubrik="frågor per vecka" />;
}

function TrendPlatshallare({ text }: { text: string }) {
  return (
    <div className="h-11 flex items-end">
      <div className="w-full border-t border-dashed border-ink-300 pt-1.5 text-[10.5px] text-ink-500">{text}</div>
    </div>
  );
}

function HeroCard({
  label,
  value,
  subValue,
  subLabel,
  delta,
  deltaLabel,
  icon,
  tone,
  compact,
  hideDelta,
  trend,
}: {
  label: string;
  value: number;
  subValue: number | null;
  subLabel: string;
  delta: number;
  deltaLabel: string;
  icon: ReactNode;
  tone: 'brand' | 'warning' | 'success';
  compact?: boolean;
  hideDelta?: boolean;
  /** Veckostaplar (eller deras platshållare) till höger om nyckeltalet. */
  trend?: ReactNode;
}) {
  const visat = useRaknaUpp(value, label);
  // Nyckeltalet står i bläcksvart oavsett ton — färgen bärs av ikonrutan, så att
  // en rad med fyra kort inte blir fyra konkurrerande färgfält.
  return (
    <div className="h-full rounded-lg border border-ink-200 bg-surface p-4">
      <div className="flex flex-col h-full">
        <div className="flex items-start justify-between gap-3 mb-3">
          <span className="text-metric-label">{label}</span>
          <IconTile size="sm" tone={tone === 'brand' ? 'brand' : tone} icon={icon} />
        </div>

        <div className="flex-1 flex items-end gap-6">
          <div className="shrink-0">
            <p
              className={cn(
                'font-semibold tracking-[-0.025em] leading-none tabular-nums text-ink-900',
                compact ? 'text-[26px]' : 'text-[30px]',
              )}
            >
              {visat.toLocaleString('sv-SE')}
            </p>
            {subValue != null && (
              <p className="text-[12px] text-ink-500 mt-2">
                <span className="text-ink-700 tabular-nums">{subValue.toLocaleString('sv-SE')}</span>{' '}
                {subLabel}
              </p>
            )}
            {subValue == null && subLabel && (
              <p className="text-[12px] text-ink-500 mt-2">{subLabel}</p>
            )}
          </div>
          {trend && <div className="flex-1 min-w-0 max-w-[280px] ml-auto">{trend}</div>}
        </div>

        <div className="mt-3 pt-2.5 border-t border-ink-200">
          <div className="flex items-center gap-1.5 text-[11.5px]">
            {hideDelta ? (
              <span className="text-ink-500">{deltaLabel}</span>
            ) : (
              <>
                <TrendingUp size={13} className="text-emerald-600" strokeWidth={2} />
                <span className="text-ink-700 tabular-nums">{delta.toLocaleString('sv-SE')}</span>
                <span className="text-ink-500">{deltaLabel}</span>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function SectionHeader({
  icon,
  tone,
  title,
  subtitle,
}: {
  icon: ReactNode;
  tone: 'brand' | 'warning' | 'neutral' | 'success';
  title: string;
  subtitle: string;
}) {
  return (
    <div className="flex items-center gap-3 mb-5">
      <IconTile size="sm" tone={tone} icon={icon} />
      <div className="flex-1 min-w-0">
        <h3 className="font-bold text-ink-900 text-[15px] leading-none">{title}</h3>
        <p className="text-[11.5px] font-semibold text-ink-500 mt-1">{subtitle}</p>
      </div>
    </div>
  );
}

function EmptyState({ text }: { text: string }) {
  return <p className="text-[13px] text-ink-500 text-center py-8 font-medium">{text}</p>;
}
